<script setup lang="ts">
import { computed, ref, watch, inject } from "vue";
import { showAddressError, markAddressTouched } from "@/composables/addressValidationSignal";
import { isCustomerSite } from "@/formEnvironment";

defineOptions({ name: "PostcodeLookup" });

const emit = defineEmits<{
    addressSelected: [address: Address];
    manualEntryRequested: [];
    lookupPostcodeRequested: [];
    clearAddress: [];
    "update:modelValue": [value: string];
}>();

const props = withDefaults(
    defineProps<{
        modelValue?: string;
        allowManualEntry?: boolean;
        searchGuard?: ((postcode: string) => string | null | undefined | false) | null;
        // Sibling Address object key on the form model, read to render the selected-address summary.
        addressPath?: string;
    }>(),
    {
        modelValue: "",
        allowManualEntry: false,
        searchGuard: null,
        addressPath: "RetailAddress",
    }
);

// ---- selected-address summary (read from the form model) ----
const vueformApi = inject<any>("vueformApi", null);
const savedAddress = computed<Record<string, any>>(() => vueformApi?.value?.model?.[props.addressPath] ?? {});
const hasAddress = computed(() => {
    const a = savedAddress.value;
    return !!(a?.Postcode && (a?.HouseNameOrNumber || a?.Street || a?.City));
});
const addressLines = computed(() => {
    const a = savedAddress.value;
    return [
        [a?.HouseNameOrNumber, a?.Street, a?.Locality].filter(Boolean).join(", "),
        [a?.City, a?.County].filter(Boolean).join(", "),
        a?.Postcode,
    ].filter((l) => l && String(l).trim());
});

// search (find a postcode) | summary (formatted, chosen address) | edit (manual fields shown below).
// `override` lets the user leave the summary; otherwise the mode follows whether an address is captured.
const override = ref<"search" | "edit" | null>(null);
const mode = computed<"search" | "summary" | "edit">(() => override.value ?? (hasAddress.value ? "summary" : "search"));

// Ask the caller-supplied guard whether the current postcode may be searched.
const searchBlockedMessage = computed<string | null>(() => {
    if (!props.searchGuard) return null;
    const postcode = (postcodeInput.value ?? "").toUpperCase().trim();
    if (!postcode) return null;
    return props.searchGuard(postcode) || null;
});

type LookupResult = { id: string; description: string };
type Address = {
  organisation?: string;
  subBuilding?: string;
  buildingName?: string;
  buildingNumber?: string;
  secondaryStreet?: string;
  street?: string;
  subLocality?: string;
  postcode?: string;
  locality?: string;
  city?: string;
  region?: string;
  countryCode?: string;
  geometry?: { coordinate?: { latitude?: number; longitude?: number } };
  uprn?: string;
};

const loading = ref(false);
const error = ref<string | null>(null);

function requestManualEntry() {
  override.value = "edit";
  markAddressTouched();
  emit("manualEntryRequested");
}

// "Search again" / "Select a different address": clear the captured address and return to the search box.
function selectDifferent() {
  results.value = [];
  selectedId.value = "";
  postcodeInput.value = "";
  override.value = "search";
  emit("clearAddress");
}

const results = ref<LookupResult[]>([]);
const selectedId = ref<string>("");
const postcodeInput = ref((props.modelValue ?? "").toUpperCase().trim());

// Persist the entered postcode back to the form model so it survives navigation. Typing also hides any
// prior submit-time address error until the next Next / capture.
watch(postcodeInput, (val) => {
  emit("update:modelValue", val);
  markAddressTouched();
});

// Restore the postcode when the form model changes (e.g. returning to this step).
watch(
  () => props.modelValue,
  (val) => {
    const incoming = val ?? "";
    if (incoming !== postcodeInput.value) {
      postcodeInput.value = incoming;
    }
  }
);

async function postcodeSearch() {
  error.value = null;
  results.value = [];
  selectedId.value = "";

  const postcode = postcodeInput.value.toUpperCase().trim();
  if (!postcode) {
    error.value = "Please provide a postcode.";
    return;
  }

  // Respect the caller-supplied guard: don't search postcodes it blocks.
  const blockedMessage = props.searchGuard?.(postcode);
  if (blockedMessage) {
    error.value = blockedMessage;
    return;
  }

  loading.value = true;
  try {
    // Customersite GeoService API: postcode in the URL (whitespace stripped server-side too).
    const res = await fetch(`/api/postcodelookup/${encodeURIComponent(postcode.replace(/\s+/g, ""))}`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (!res.ok) throw new Error(`postcodelookup failed (${res.status})`);
    const data = await res.json();

    // FindResponse serialises PascalCase (Results / Id / Description); tolerate camelCase too.
    const raw: any[] = data?.Results ?? data?.results ?? [];
    results.value = raw
      .map((r: any) => ({ id: r.Id ?? r.id, description: r.Description ?? r.description }))
          .filter((r: LookupResult) => !!r.id);
    if (!results.value.length) error.value = "No addresses found for that postcode.";
  } catch (e: any) {
    error.value = "Postcode lookup is unavailable. Please, try again later.";
    console.error(e);
  } finally {
    loading.value = false;
  }
}

async function fetchAddressById(id: string) {
  if (!id) return;

  error.value = null;
  loading.value = true;
  try {
    const res = await fetch(`/api/postcodelookup/address/${encodeURIComponent(id)}`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (!res.ok) throw new Error(`address lookup failed (${res.status})`);
    // RetrieveResponse serialises PascalCase; normalise to the shape the schema's mapper reads
    // (Locality = post town → city; Region = county → region).
    const r: any = await res.json();
    const address: Address = {
      organisation: r.Organisation ?? r.organisation,
      subBuilding: r.SubBuilding ?? r.subBuilding,
      buildingName: r.BuildingName ?? r.buildingName,
      buildingNumber: r.BuildingNumber ?? r.buildingNumber,
      secondaryStreet: r.SecondaryStreet ?? r.secondaryStreet,
      street: r.Street ?? r.street,
      locality: r.SubLocality ?? r.subLocality ?? "",
      city: r.Locality ?? r.locality ?? "",
      region: r.Region ?? r.region ?? "",
      postcode: r.Postcode ?? r.postcode,
      countryCode: r.CountryCode ?? r.countryCode,
      uprn: r.Uprn ?? r.uprn,
      geometry: r.Geometry ?? r.geometry,
    };

    emit("addressSelected", address);
    // The schema has populated the model — fall back to the summary view.
    override.value = null;
    results.value = [];
  } catch (e) {
    error.value = "Address lookup is unavailable. Please, try again later.";
    console.error(e);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="row addressWrapper" :class="{ 'address-show-error': showAddressError }">
    <!-- Selected address summary (Markel Direct "Address added"): formatted, read-only, with Edit / Search again. -->
    <div v-if="mode === 'summary'" class="col-12">
      <div class="address-summary">
        <p class="address-summary-added"><em class="far fa-circle-check mR-5"></em> Address added</p>
        <p class="address-summary-lines">
          <span v-for="(line, i) in addressLines" :key="i">{{ line }}</span>
        </p>
        <div class="address-summary-actions">
          <button type="button" class="btn btn-ghost btn-sm" @click="selectDifferent">
            <em class="far fa-magnifying-glass mR-5"></em> Search again
          </button>
          <button type="button" class="btn btn-ghost btn-sm" @click="requestManualEntry">
            <em class="far fa-pen-to-square mR-5"></em> Edit
          </button>
        </div>
      </div>
    </div>

    <!-- Search: postcode box + results dropdown + "enter manually" (hidden once an address is captured). -->
    <div v-else-if="mode === 'search'" class="col-12">
      <div class="row form-group">
        <div class="col-sm-12 col-md-6">
          <div class="input-group">
            <input
              type="search"
              class="form-control text-uppercase"
              v-model="postcodeInput"
              id="correspondencePostcode"
              placeholder="e.g. WR5 3DA"
              @keydown.enter.prevent="postcodeSearch"
            />
            <div class="input-group-append">
              <button type="button" class="btn btn-outline-secondary pcl-find" @click="postcodeSearch" :disabled="loading || !!searchBlockedMessage || !postcodeInput.trim()">
                <em v-show="loading" class="far fa-circle-notch fa-spin mR-5"></em> <span class="pcl-find-long">Find address</span><span class="pcl-find-short">Find</span>
              </button>
            </div>
          </div>

          <div v-if="searchBlockedMessage" class="small text-danger mt-1">{{ searchBlockedMessage }}</div>
          <div v-else-if="error" class="small text-danger mt-1">{{ error }}</div>

          <div v-if="results.length" class="address-results">
            <select
              class="form-control"
              v-model="selectedId"
              @change="fetchAddressById(selectedId)"
              :disabled="loading"
            >
              <option value="">Please pick an address</option>
              <option v-for="r in results" :key="r.id" :value="r.id">
                {{ r.description }}
              </option>
            </select>
          </div>

          <button
            v-if="allowManualEntry"
            type="button"
            class="btn btn-ghost btn-sm mt-2"
            @click="requestManualEntry"
          >
            Add address manually <em class="far fa-pen-to-square mL-5"></em>
          </button>

          <p v-if="isCustomerSite" class="address-uk-note">
            Please note that our policies are only available to businesses based in the UK. If your business is not based in the UK, our policy is not suitable for you.
          </p>
        </div>
      </div>
    </div>

    <!-- Edit: the schema's manual address fields render below; offer a way back to search. -->
    <div v-else class="col-12">
      <button type="button" class="btn btn-ghost btn-sm mb-2" @click="selectDifferent">
        <em class="far fa-magnifying-glass mR-5"></em> Search for a different address
      </button>
    </div>
  </div>
</template>
